import React from 'react';
import PropTypes from 'prop-types';
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import {
    isEmpty,
    extractWorkSheetData,
    getWithExpiry,
    formatDate,
    ExcelDateToDateString
} from "../../../globals/GlobalFunctions";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import axios from 'axios';
import {refreshToken} from "../../../globals/GlobalFunctions";
import Autocomplete from "@mui/material/Autocomplete";
import {GlobalConstants} from "../../../globals/GlobalConstants";
import TextField from "@mui/material/TextField";
import LoadingButton from "@mui/lab/LoadingButton";
import SaveIcon from "@mui/icons-material/Save";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";

export interface Props {
    excelData: any;
    currentDB: string;
}

function BulkIDCodeImporter({excelData, currentDB}: Props) {
    const [successAlert, setSuccessAlert] = React.useState(false);
    const [errorAlert, setErrorAlert] = React.useState(false);
    const [validationText, setValidationText] = React.useState("");
    const [validationError, setValidationError] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [currentIDCodeValue, setCurrentIDCodeValue] = React.useState({group_ID: "",
        plan_ID: "", iD_Code: "", iD_Code_Data: "", iD_Code_Data_Option: false, vbasoftware_database: "CoastalAdminTest", vbapi_Token: "", iD_Code_Data_Date: ""});

    let token = localStorage.getItem('jwt');

    const handleValidationClose = () => {
        setValidationError(false);
        setValidationText("");
    }

    const initialCodeState = {
        group_ID: "",
        plan_ID: "",
        iD_Code: "",
        iD_Code_Data: "",
        iD_Code_Data_Option: false,
        vbasoftware_database: "CoastalAdminTest",
        vbapi_Token: "",
        iD_Code_Data_Date: ""
    }

    const clearIDCodes = () => {
        setCurrentIDCodeValue({...initialCodeState});
    }

    const importIDCodes = async (idCodeArray : any[]) => {
        refreshToken();
        setIsLoading(true);
        const response =
            await axios
                .put(
                    localStorage.getItem('baseURL') + 'idCode/putList',
                    idCodeArray,
                    {headers: {'Authorization': `Bearer ${token}`, accept: "*/*", "Content-Type": "application/json"}}
                ).then(() => {
                    setLoading(false);
                    setSuccessAlert(true);
                    setIsLoading(false);
                }).catch((error) => {
                    //console.log(response);
                    setLoading(false);
                    setValidationText(error.response.request.responseText);
                    setValidationError(true);
                    setIsLoading(false);
                });
        //console.log(response);
    }

    try {
        //Check for data and that there is a sheet in the data named IDCodeValue
        if (!isEmpty(excelData) && excelData.Sheets.IDCodeValue !== undefined) {
            //console.log(excelData, "Made it to formatter!");

            //global function to extract the data into a sequential array of cell data values as well as the column and row ranges
            let sheetDataArray = extractWorkSheetData(excelData.Sheets.IDCodeValue);

            let rawCellArray: any[] = sheetDataArray[0];

            let columnRange: number = sheetDataArray[1];

            let rowRange: number = sheetDataArray[2];

            let idCodeArray: any[] = [];

            //console.log(rawCellArray, "Raw Data");

            //we need to find the column index of the values we require so we can grab the data for each row -- THIS IS WHERE COLUMN NAMES ARE SET
            //we add 1 for each for the loop multiplier to account for it being zero based, but later we subtract 1 to account for the very fist row actually starting at zero
            const idCodeIndexCollection = {
                groupIDIndex: rawCellArray.indexOf('Group') + 1,
                planIDIndex: rawCellArray.indexOf('Plan') + 1,
                idCodeIndex: rawCellArray.indexOf('Vendor') + 1,
                iDCodeDataIndex: rawCellArray.indexOf('Data Value') + 1,
                iDCodeDataOptionIndex: rawCellArray.indexOf('Option On/Off') + 1,
                iDCodeDataDate: rawCellArray.indexOf('Data Date') + 1
            }

            if(idCodeIndexCollection.groupIDIndex > 0 && idCodeIndexCollection.planIDIndex > 0 && idCodeIndexCollection.idCodeIndex > 0 &&
                idCodeIndexCollection.iDCodeDataIndex > 0 && idCodeIndexCollection.iDCodeDataOptionIndex > 0) {

                for (let i = 1; i < rowRange; i++) {
                    //for each new member array we skip over the previous groups determined by the column length, removing 1 to account for our zero based index at the beginning
                    let rowMultiplier = columnRange * i - 1;

                    //start with group ID
                    if (idCodeIndexCollection.groupIDIndex > 0) {
                        currentIDCodeValue.group_ID = rawCellArray[(rowMultiplier + idCodeIndexCollection.groupIDIndex)].toString().toUpperCase();
                    }

                    //Plan ID
                    if (idCodeIndexCollection.planIDIndex > 0) {
                        currentIDCodeValue.plan_ID = rawCellArray[(rowMultiplier + idCodeIndexCollection.planIDIndex)].toString().toUpperCase();
                    }

                    //Option ON/OFF
                    if (idCodeIndexCollection.iDCodeDataOptionIndex > 0) {
                        if (rawCellArray[(rowMultiplier + idCodeIndexCollection.iDCodeDataOptionIndex)].toString().toUpperCase() === "YES" ||
                            rawCellArray[(rowMultiplier + idCodeIndexCollection.iDCodeDataOptionIndex)].toString().toUpperCase() === "Y" ||
                            rawCellArray[(rowMultiplier + idCodeIndexCollection.iDCodeDataOptionIndex)].toString().toUpperCase() === "ON") {
                            currentIDCodeValue.iD_Code_Data_Option = true;
                        }
                        else {
                            currentIDCodeValue.iD_Code_Data_Option = false;
                        }
                    }

                    //Vendor
                    if (idCodeIndexCollection.idCodeIndex > 0) {
                        currentIDCodeValue.iD_Code = rawCellArray[(rowMultiplier + idCodeIndexCollection.idCodeIndex)].toString().toUpperCase();
                    }

                    //Data Option
                    if (idCodeIndexCollection.iDCodeDataIndex > 0) {
                        currentIDCodeValue.iD_Code_Data = rawCellArray[(rowMultiplier + idCodeIndexCollection.iDCodeDataIndex)].toString().toUpperCase();
                    }

                    //Original Date
                    if (idCodeIndexCollection.iDCodeDataDate > 0) {
                        let date = rawCellArray[(rowMultiplier + idCodeIndexCollection.iDCodeDataDate)].toString();
                        if (date.includes("/")) {
                            date = formatDate(date);
                        } else if (date.length > 0 && date.length < 6) {
                            date = ExcelDateToDateString(date);
                        }
                        if (date.includes("-")) {
                            date = date.substring(5, 7) + "/" + date.substring(8, 10) + "/" + date.substring(0, 4);
                        }
                        else if (date.length > 7) {
                            date = date.substring(4, 6) + "/" + date.substring(6, 8) + "/" + date.substring(0, 4);
                        }
                        currentIDCodeValue.iD_Code_Data_Date = date.toString().toUpperCase();
                    }

                    currentIDCodeValue.vbasoftware_database = currentDB;

                    currentIDCodeValue.vbapi_Token = getWithExpiry('vbapi_Token');

                    if((currentIDCodeValue.group_ID.length > 0 || currentIDCodeValue.plan_ID.length > 0) && currentIDCodeValue.iD_Code.length > 0){
                        idCodeArray.push({...currentIDCodeValue});
                    }
                    else {
                        // setValidationText("Group ID, Plan ID, and Vendor are required.");
                        // setValidationError(true);
                    }

                    currentIDCodeValue.group_ID = initialCodeState.group_ID;
                    currentIDCodeValue.plan_ID = initialCodeState.plan_ID;
                    currentIDCodeValue.iD_Code = initialCodeState.iD_Code;
                    currentIDCodeValue.iD_Code_Data = initialCodeState.iD_Code_Data;
                    currentIDCodeValue.iD_Code_Data_Option = initialCodeState.iD_Code_Data_Option;
                    currentIDCodeValue.iD_Code_Data_Date = initialCodeState.iD_Code_Data_Date;

                    clearIDCodes();
                }


                importIDCodes(idCodeArray);

                excelData.Sheets.IDCodeValue = undefined;
            }
            else {
                //setValidationText("Group ID, Plan ID, and Vendor are required.");
                //setValidationError(true);
            }

        }
    }
    catch(e) {
        console.log("ERROR", e);

        setErrorAlert(true);
        //clear it out after errors in case of rerendering otherwise the error spams will continue until morale improves
        excelData.Sheets.IDCodeValue = undefined;
    }

    return (
        <div>
            {successAlert ? <Alert className="groupAlertStyles" severity='success' onClose={() => setSuccessAlert(false)} variant="filled">
                <AlertTitle>Success!</AlertTitle>
                IDCode Import Complete!
            </Alert> : <></> }
            {errorAlert ? <Alert className="groupAlertStyles" severity='error' onClose={() => setErrorAlert(false)} variant="filled">
                <AlertTitle>Error!</AlertTitle>
                There was an error with this file!
            </Alert> : <></> }
            <Dialog
                open={validationError}
                onClose={handleValidationClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description">
                <DialogTitle id="alert-dialog-title">
                    {"Validation Error"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {validationText}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleValidationClose}>OK</Button>
                </DialogActions>
            </Dialog>
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={isLoading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </div>
    );
}

export default React.memo(BulkIDCodeImporter)

BulkIDCodeImporter.propTypes = {
    excelData: PropTypes.object.isRequired
};
